import { useQuery } from "@tanstack/react-query";
import { apiGet } from "./client";
import { useTicketSize } from "../ticket-size";
import type { SparkPoint } from "./shared";

export interface EarningsReturnSummary {
  netInvestment: string;
  unearnedInterest: string;
  earnedInterest: string;
  riskAdjustedYield: {
    current: string;
    trend: { value: string; direction: "up" | "down" | "flat" };
    /** the text shown next to the change, e.g. "vs Aug'26" */
    comparison?: string | null;
  };
  earningsTrend: Array<{
    month: string;
    totalEarningsProj: number;
    repeatCustomerEarnings: number;
    chargeoffEarningsLoss: number;
    prepaymentEarningsLoss: number;
  }>;
  cards: {
    prepayment: { stat: string; sparkline: SparkPoint[] };
    /** Repeat rate for `windowMonths` (48, or the longest window the table has); trend is vs the previous file date.
     *  stat is "--" while the table has no data. */
    repeatCustomer: {
      stat: string;
      sparkline: SparkPoint[];
      trend: { value: string; direction: "up" | "down" | "flat" } | null;
      windowMonths: number | null;
    };
  };
}

export function useEarningsReturn() {
  const { ticket } = useTicketSize();
  return useQuery({
    queryKey: ["earnings-return", ticket ?? "all"],
    queryFn: () => apiGet<EarningsReturnSummary>("/earnings-return", { ticket }),
  });
}

export interface ProjectionPoint {
  month: string;
  value: number;
}

// Each series is null when its source column has no data at all -- rendered as "Not Available Yet."
export interface EarningsProjections {
  expectedRepeatCustomers: ProjectionPoint[] | null;
  expectedPrepayments: ProjectionPoint[] | null;
  expectedLossChargeoffs: ProjectionPoint[] | null;
}

// Own request, separate from useEarningsReturn: it scans 24 file dates, so the stat tiles
// shouldn't wait on it (or fail with it).
export function useEarningsProjections() {
  const { ticket } = useTicketSize();
  return useQuery({
    queryKey: ["earnings-return", "projections", ticket ?? "all"],
    queryFn: () => apiGet<EarningsProjections>("/earnings-return/projections", { ticket }),
  });
}
